import { useState, useEffect, useRef } from 'react';
import {
  Paper,
  Stack,
  Title,
  Group,
  Text,
  Button,
  Divider,
  Alert,
  LoadingOverlay,
  Modal,
  FileInput,
  Badge,
  ActionIcon,
  Card,
  Radio,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconUpload,
  IconFile,
  IconX,
  IconCheck,
  IconCamera,
  IconTrash,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useMedplum } from '@medplum/react';
import { notifications } from '@mantine/notifications';
import { DocumentScannerModal } from '@/components/DocumentScanner/DocumentScannerModal';
import { ConsentData, ConsentSectionProps } from '@/types/consent.types';
import { useConsent } from '@hooks/useConsent';
import { useConsentOperations } from '@hooks/useConsentOperations';
import { DateInputWrapper } from '@/components/inputs/DateInputWrapper';
import { validateConsentData } from '@utils/consent-validation';
import { ConsentSignerSection } from './ConsentSignerSection';
import { EXTERNAL_CS, SYSTEMS } from '@/consts/systems.consts';
import { useConceptMap } from '../hooks/useConceptMap';
import FormActionButtons, {
  FormActionButtonsRef,
} from '@/components/FormActionButtons';
import { isEqual } from 'lodash';
import { useDateDisplay } from '@hooks/useDateDisplay';

const ConsentCard = ({
  title,
  description,
  required = false,
  value,
  onYes,
  onNo,
  date,
  onDateChange,
  disabled = false,
}: {
  title: string;
  description: string;
  required?: boolean;
  value: boolean | null;
  onYes: () => void;
  onNo: () => void;
  date?: Date | null;
  onDateChange?: (date: Date | null) => void;
  disabled?: boolean;
}) => {
  const { t } = useTranslation(['lims', 'common']);
  const getStatusBadge = () => {
    if (value === true) {
      return (
        <Badge
          variant="light"
          size="xs"
          color="green"
          leftSection={<IconCheck size={10} />}
        >
          {t('consent.display.accepted', { ns: 'lims' })}
        </Badge>
      );
    } else if (value === false) {
      return (
        <Badge
          variant="light"
          size="xs"
          color="red"
          leftSection={<IconX size={10} />}
        >
          {t('consent.display.declined', { ns: 'lims' })}
        </Badge>
      );
    } else {
      return (
        <Badge variant="light" size="xs" color="gray">
          {t('consent.display.notAnswered', { ns: 'lims' })}
        </Badge>
      );
    }
  };

  return (
    <Card withBorder shadow="sm" p="md">
      <Stack gap="xs">
        <Group gap="0px">
          <Text fw={500} size="sm">
            {title}
          </Text>
          {required && (
            <Text c="red" size="sm" ml="4px" mr="sm" span>
              *
            </Text>
          )}
          {getStatusBadge()}
        </Group>
        <Text size="sm" c="dimmed">
          {description}
        </Text>
        <Group wrap="nowrap" gap="md">
          <Radio.Group
            value={value === true ? 'yes' : value === false ? 'no' : ''}
            onChange={(val) => {
              if (val === 'yes') {
                onYes();
              } else if (val === 'no') {
                onNo();
              }
            }}
          >
            <Group>
              <Radio
                value="yes"
                label={t('yes', { ns: 'common' })}
                color="green"
                disabled={disabled}
              />
              <Radio
                value="no"
                label={t('no', { ns: 'common' })}
                color="red"
                disabled={disabled}
              />
            </Group>
          </Radio.Group>

          {date !== undefined && onDateChange && (
            <DateInputWrapper
              placeholder="Date Signed"
              value={date}
              onChange={onDateChange}
              disabled={disabled || value === null}
              style={{ flex: 1 }}
            />
          )}
        </Group>
      </Stack>
    </Card>
  );
};

export function ConsentSection({
  serviceRequest,
  patient,
  onConsentUpdate,
  onCloseModal,
  editMode = false,
  showModal = false,
}: ConsentSectionProps) {
  const { t } = useTranslation(['lims', 'common']);
  const formatDate = useDateDisplay();
  const medplum = useMedplum();
  const [consentFiles, setConsentFiles] = useState<File[]>([]);
  const [scanTarget, setScanTarget] = useState<'consent' | 'authorization'>(
    'consent'
  );
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [documentsToDelete, setDocumentsToDelete] = useState<string[]>([]);
  const [scannerModalOpened, setScannerModalOpened] = useState(false);
  const baselineRef = useRef<ConsentData>({
    general: null,
    generalDate: null,
    scientificUse: null,
    scientificUseDate: null,
    photoVideoUse: null,
    incidentalFindings: null,
    incidentalFindingsDate: null,
    secondaryFindings: null,
    secondaryFindingsDate: null,
    consentFiles: [],
    // Signer information
    signerType: null,
    selectedRelatedPerson: undefined,
    newRelatedPersonName: undefined,
    newRelatedPersonRelationship: undefined,
    existingRelatedPersonIdToDelete: undefined,
  });
  const formActionsRef = useRef<FormActionButtonsRef>(null);

  // Use ConceptMap hook to get consent requirements
  const {
    conceptMap,
    error: conceptMapError,
    isConsentRequired,
    validateRequiredConsents,
  } = useConceptMap();

  // Use the custom hook for existing consent loading
  const {
    consent: existingConsent,
    consentData: hookConsentData,
    documents: linkedDocuments,
    refetch,
  } = useConsent(serviceRequest);

  // Use the consent operations hook
  const { handleSaveConsent: saveConsent, loading } = useConsentOperations({
    serviceRequest,
    patient,
    existingConsentId: existingConsent?.id,
    existingConsentMeta: existingConsent?.meta,
    onConsentUpdate,
    onCloseModal,
    showModal,
  });

  // Get order creation date for default consent dates
  const orderCreationDate = serviceRequest.authoredOn
    ? new Date(serviceRequest.authoredOn)
    : new Date();

  // Local state for form data (initialized from hook data)
  const [consentData, setConsentData] = useState<ConsentData>({
    general: null,
    generalDate: null,
    scientificUse: null,
    scientificUseDate: null,
    photoVideoUse: null,
    incidentalFindings: null,
    incidentalFindingsDate: null,
    secondaryFindings: null,
    secondaryFindingsDate: null,
    consentFiles: [],
    // Signer information
    signerType: null,
    selectedRelatedPerson: undefined,
    newRelatedPersonName: undefined,
    newRelatedPersonRelationship: undefined,
    existingRelatedPersonIdToDelete: undefined,
  });

  // Update form data when hook data changes
  useEffect(() => {
    if (hookConsentData) {
      setConsentData(hookConsentData);
      // Clear documents marked for deletion when loading new consent data
      setDocumentsToDelete([]);
      // capture baseline once loaded
      baselineRef.current = hookConsentData;
    }
  }, [hookConsentData]);

  const handleSaveConsent = async () => {
    // Check if ConceptMap is loaded
    if (!conceptMap) {
      setValidationErrors([
        t('consent.validation.conceptMapNotLoaded', '', { ns: 'lims' }),
      ]);
      return;
    }

    // Validate consent data using ConceptMap requirements
    const conceptMapValidation = validateRequiredConsents(
      serviceRequest,
      consentData
    );
    if (!conceptMapValidation.isValid) {
      const missingConsentErrors = conceptMapValidation.missingConsents.map(
        (consentCode) =>
          t(`consent.validation.missingConsent.${consentCode}`, {
            ns: 'lims',
            defaultValue: `Missing required consent: ${consentCode}`,
          })
      );
      setValidationErrors(missingConsentErrors);
      return;
    }

    // Also run the existing validation for other checks
    // Calculate existing authorization documents for validation
    const existingAuthorizationDocumentCount = linkedDocuments.filter(
      (doc) =>
        doc.type?.coding?.[0]?.code ===
          EXTERNAL_CS.LOINC.CODES.AUTHORIZATION_DOCUMENT &&
        doc.type?.coding?.[0]?.system === SYSTEMS.LOINC &&
        doc.id &&
        !documentsToDelete.includes(doc.id)
    ).length;

    const validation = validateConsentData(consentData, {
      t: t as any,
      existingAuthorizationDocumentCount,
    });
    if (!validation.isValid) {
      setValidationErrors(validation.errors);
      return;
    }

    const existingConsentFiles = linkedDocuments.filter(
      (doc) =>
        doc.type?.coding?.[0]?.code ===
          EXTERNAL_CS.LOINC.CODES.CONSENT_DOCUMENT &&
        doc.type?.coding?.[0]?.system === SYSTEMS.LOINC &&
        doc.id &&
        !documentsToDelete.includes(doc.id)
    );

    const hasFiles = consentFiles.length > 0 || existingConsentFiles.length > 0;

    if (!hasFiles) {
      setValidationErrors([
        t('consent.validation.missingConsent.consentFileRequired', {
          ns: 'lims',
          defaultValue: 'At least one consent document file must be uploaded',
        }),
      ]);
      return;
    }

    // Clear validation errors if validation passes
    setValidationErrors([]);

    try {
      // Delete documents marked for deletion
      for (const documentId of documentsToDelete) {
        try {
          await medplum.deleteResource('DocumentReference', documentId);
        } catch (error) {
          console.error(`Error deleting document ${documentId}:`, error);
          // Continue with other deletions even if one fails
        }
      }
      // Extract existing consent files from linked documents
      const existingConsentFiles = linkedDocuments
        .filter(
          (doc) =>
            doc.type?.coding?.[0]?.code ===
              EXTERNAL_CS.LOINC.CODES.CONSENT_DOCUMENT &&
            doc.type?.coding?.[0]?.system === SYSTEMS.LOINC &&
            doc.id &&
            !documentsToDelete.includes(doc.id)
        )
        .map((doc) => doc.id as string);

      // Save consent with new data and existing consent files
      const success = await saveConsent(
        consentData,
        consentFiles,
        existingConsentFiles
      );
      if (success) {
        // Clear consent files and documents to delete after successful save
        setConsentFiles([]);
        setDocumentsToDelete([]);
        await refetch();
      }
    } catch (error) {
      console.error('Error during save process:', error);
      notifications.show({
        title: t('error', { ns: 'common' }),
        message: t('consent.notifications.saveError', { ns: 'lims' }),
        color: 'red',
      });
    }
  };

  const handleFileUpload = (files: File | File[] | null) => {
    if (files) {
      const fileArray = Array.isArray(files) ? files : [files];
      setConsentFiles((prev) => [...prev, ...fileArray]);
    }
  };

  const removeFile = (index: number) => {
    setConsentFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleScanComplete = (scannedFile: File) => {
    if (scanTarget === 'authorization') {
      updateConsentData({ authorizationDocument: scannedFile });
    } else {
      setConsentFiles((prev) => [...prev, scannedFile]);
    }
    setScannerModalOpened(false);
  };

  const handleDeleteExistingDocument = (documentId: string) => {
    setDocumentsToDelete((prev) => [...prev, documentId]);
  };

  const updateConsentData = (updates: Partial<ConsentData>) => {
    setConsentData((prev) => ({ ...prev, ...updates }));
  };

  const hasUnsavedChanges = (): boolean => {
    try {
      const current = consentData;
      const baseline = baselineRef.current;
      return baseline && !isEqual(current, baseline);
    } catch {
      return true;
    }
  };

  const handleClose = () => {
    if (onCloseModal) {
      onCloseModal();
    }
  };

  const handleDiscardChanges = () => {
    if (baselineRef.current) {
      setConsentData(baselineRef.current);
    }
  };

  const isReadOnly = !editMode || serviceRequest.status === 'completed';

  const consentForm = (
    <>
      <LoadingOverlay visible={loading} />

      <Stack gap="md">
        <Group justify="space-between">
          <Title order={3}>{t('consent.title', { ns: 'lims' })}</Title>
          {existingConsent?.meta?.lastUpdated && (
            <Text size="sm" c="dimmed">
              {t('consent.lastUpdated', { ns: 'lims' })}:{' '}
              {formatDate(existingConsent.meta.lastUpdated)}
            </Text>
          )}
        </Group>

        <Divider />

        {conceptMapError && (
          <Alert icon={<IconAlertCircle size={16} />} color="red">
            {conceptMapError}
          </Alert>
        )}

        {isReadOnly && !existingConsent && (
          <Alert icon={<IconAlertCircle size={16} />} color="yellow">
            {t('consent.noConsent', { ns: 'lims' })}
          </Alert>
        )}

        <Stack gap="lg">
          {/* Validation Errors */}
          {validationErrors.length > 0 && (
            <Alert icon={<IconAlertCircle size={16} />} color="red">
              <Stack gap="xs">
                {validationErrors.map((error, index) => (
                  <Text key={index} size="sm">
                    {error}
                  </Text>
                ))}
              </Stack>
            </Alert>
          )}

          {/* Signer Information Section */}
          <ConsentSignerSection
            consentData={consentData}
            updateConsentData={updateConsentData}
            linkedDocuments={linkedDocuments}
            documentsToDelete={documentsToDelete}
            setDocumentsToDelete={setDocumentsToDelete}
            isReadOnly={isReadOnly}
            patient={patient}
            onScanClick={() => {
              setScanTarget('authorization');
              setScannerModalOpened(true);
            }}
          />

          <Divider />

          <ConsentCard
            title={t('consent.sections.general.title', { ns: 'lims' })}
            description={t('consent.sections.general.description', {
              ns: 'lims',
            })}
            required={isConsentRequired(serviceRequest, 'general-consent')}
            value={consentData.general}
            onYes={() =>
              updateConsentData({
                general: true,
                generalDate: orderCreationDate,
                scientificUse: null,
                scientificUseDate: orderCreationDate,
                photoVideoUse: null,
                incidentalFindings: null,
                incidentalFindingsDate: orderCreationDate,
                secondaryFindings: null,
                secondaryFindingsDate: orderCreationDate,
              })
            }
            onNo={() =>
              updateConsentData({
                general: false,
                generalDate: orderCreationDate,
                scientificUse: false,
                scientificUseDate: orderCreationDate,
                photoVideoUse: false,
                incidentalFindings: false,
                incidentalFindingsDate: orderCreationDate,
                secondaryFindings: false,
                secondaryFindingsDate: orderCreationDate,
              })
            }
            date={consentData.generalDate}
            onDateChange={(date) =>
              updateConsentData({
                generalDate: date,
                scientificUseDate: date,
                incidentalFindingsDate: date,
                secondaryFindingsDate: date,
              })
            }
            disabled={isReadOnly}
          />

          {consentData.general && (
            <Stack mx="xl">
              {/* Scientific Use Section */}
              <ConsentCard
                title={t('consent.sections.scientificUse.title', {
                  ns: 'lims',
                })}
                description={t('consent.sections.scientificUse.description', {
                  ns: 'lims',
                })}
                required={isConsentRequired(serviceRequest, 'scientific-use')}
                value={consentData.scientificUse}
                onYes={() =>
                  updateConsentData({
                    scientificUse: true,
                  })
                }
                onNo={() =>
                  updateConsentData({
                    scientificUse: false,
                    photoVideoUse: false,
                  })
                }
                date={consentData.scientificUseDate}
                onDateChange={(date) =>
                  updateConsentData({ scientificUseDate: date })
                }
                disabled={isReadOnly}
              />

              {/* Photo/Video Use Section - only show if required for this exam type */}
              {isConsentRequired(serviceRequest, 'photo-video') && (
                <ConsentCard
                  title={t('consent.sections.photoVideo.checkbox', {
                    ns: 'lims',
                  })}
                  description={t('consent.sections.photoVideo.description', {
                    ns: 'lims',
                  })}
                  required={isConsentRequired(serviceRequest, 'photo-video')}
                  value={consentData.photoVideoUse}
                  onYes={() =>
                    updateConsentData({
                      photoVideoUse: true,
                    })
                  }
                  onNo={() =>
                    updateConsentData({
                      photoVideoUse: false,
                    })
                  }
                  disabled={isReadOnly}
                />
              )}

              {/* Incidental Findings - only show if required for this exam type */}
              {isConsentRequired(serviceRequest, 'incidental-findings') && (
                <ConsentCard
                  title={t('consent.sections.incidentalFindings.title', {
                    ns: 'lims',
                  })}
                  description={t(
                    'consent.sections.incidentalFindings.description',
                    { ns: 'lims' }
                  )}
                  required={isConsentRequired(
                    serviceRequest,
                    'incidental-findings'
                  )}
                  value={consentData.incidentalFindings}
                  onYes={() =>
                    updateConsentData({
                      incidentalFindings: true,
                    })
                  }
                  onNo={() =>
                    updateConsentData({
                      incidentalFindings: false,
                    })
                  }
                  date={consentData.incidentalFindingsDate}
                  onDateChange={(date) =>
                    updateConsentData({ incidentalFindingsDate: date })
                  }
                  disabled={isReadOnly}
                />
              )}

              {/* Secondary Findings - only show if required for this exam type */}
              {isConsentRequired(serviceRequest, 'secondary-findings') && (
                <ConsentCard
                  title={t('consent.sections.secondaryFindings.title', {
                    ns: 'lims',
                  })}
                  description={t('consent.sections.secondaryFindings.note', {
                    ns: 'lims',
                  })}
                  required={isConsentRequired(
                    serviceRequest,
                    'secondary-findings'
                  )}
                  value={consentData.secondaryFindings}
                  onYes={() =>
                    updateConsentData({
                      secondaryFindings: true,
                    })
                  }
                  onNo={() =>
                    updateConsentData({
                      secondaryFindings: false,
                    })
                  }
                  date={consentData.secondaryFindingsDate}
                  onDateChange={(date) =>
                    updateConsentData({ secondaryFindingsDate: date })
                  }
                  disabled={isReadOnly}
                />
              )}
            </Stack>
          )}
        </Stack>

        {!isReadOnly && (
          <>
            <Divider />

            {/* File Upload Section */}
            <Stack gap="sm">
              <Text size="sm" fw={500}>
                {t('consent.fileUpload.title', { ns: 'lims' })}
                {
                  <Text component="span" c="red" ml="4px">
                    *
                  </Text>
                }
              </Text>
              <Text size="sm" c="dimmed">
                {t('consent.fileUpload.description', {
                  ns: 'lims',
                })}
              </Text>

              <Group gap="sm" grow>
                <Button
                  variant="light"
                  leftSection={<IconCamera size={18} />}
                  onClick={() => setScannerModalOpened(true)}
                  disabled={loading}
                >
                  {t('documentScanner.title', {
                    ns: 'common',
                  })}
                </Button>

                <FileInput
                  placeholder={t('consent.fileUpload.placeholder', {
                    ns: 'lims',
                  })}
                  leftSection={<IconUpload size={18} />}
                  accept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/*"
                  multiple
                  onChange={handleFileUpload}
                  disabled={loading}
                  value={undefined}
                  key={consentFiles.length}
                />
              </Group>

              {consentFiles.length > 0 && (
                <Stack gap="xs">
                  {consentFiles.map((file, index) => (
                    <Group
                      key={index}
                      justify="space-between"
                      p="xs"
                      bd="1px solid #e0e0e0"
                    >
                      <Group gap="xs">
                        <IconFile size={16} />
                        <Text size="sm">{file.name}</Text>
                        <Badge size="sm" variant="light">
                          {(file.size / 1024).toFixed(1)} KB
                        </Badge>
                      </Group>
                      <ActionIcon
                        size="sm"
                        color="red"
                        variant="subtle"
                        onClick={() => removeFile(index)}
                      >
                        <IconX size={14} />
                      </ActionIcon>
                    </Group>
                  ))}
                </Stack>
              )}

              {/* Display existing consent files */}
              {linkedDocuments.filter(
                (doc) =>
                  doc.type?.coding?.[0]?.code ===
                    EXTERNAL_CS.LOINC.CODES.CONSENT_DOCUMENT &&
                  doc.type?.coding?.[0]?.system === SYSTEMS.LOINC &&
                  !documentsToDelete.includes(doc.id || '')
              ).length > 0 && (
                <>
                  <Text size="sm" fw={500} mt="sm">
                    {t('consent.fileUpload.existingDocuments', { ns: 'lims' })}
                  </Text>
                  <Stack gap="xs">
                    {linkedDocuments
                      .filter(
                        (doc) =>
                          doc.type?.coding?.[0]?.code ===
                            EXTERNAL_CS.LOINC.CODES.CONSENT_DOCUMENT &&
                          doc.type?.coding?.[0]?.system === SYSTEMS.LOINC &&
                          !documentsToDelete.includes(doc.id || '')
                      )
                      .map((doc, index) => (
                        <Group
                          key={index}
                          justify="space-between"
                          p="xs"
                          bd="1px solid #e0e0e0"
                        >
                          <Group gap="xs">
                            <IconFile size={16} />
                            <Text size="sm">
                              {doc.content?.[0]?.attachment?.title ||
                                doc.description ||
                                `Document ${index + 1}`}
                            </Text>
                            {doc.date && (
                              <Badge size="sm" variant="light">
                                {formatDate(doc.date)}
                              </Badge>
                            )}
                          </Group>
                          {doc.id && (
                            <ActionIcon
                              size="sm"
                              color="red"
                              variant="subtle"
                              onClick={() =>
                                doc.id && handleDeleteExistingDocument(doc.id)
                              }
                            >
                              <IconTrash size={14} />
                            </ActionIcon>
                          )}
                        </Group>
                      ))}
                  </Stack>
                </>
              )}
            </Stack>

            {showModal ? (
              <FormActionButtons
                ref={formActionsRef}
                onSave={handleSaveConsent}
                onDiscard={handleDiscardChanges}
                onClose={handleClose}
                hasUnsavedChanges={hasUnsavedChanges()}
                isLoading={loading}
                saveLabel={t('consent.saveButton', { ns: 'lims' })}
              />
            ) : (
              <Group justify="flex-end" mt="md">
                <Button
                  onClick={handleSaveConsent}
                  loading={loading}
                  leftSection={<IconCheck size={16} />}
                >
                  {t('consent.saveButton', { ns: 'lims' })}
                </Button>
              </Group>
            )}
          </>
        )}
      </Stack>

      {/* Document Scanner Modal */}
      <DocumentScannerModal
        opened={scannerModalOpened}
        onClose={() => setScannerModalOpened(false)}
        onScanComplete={handleScanComplete}
        preferredCamera="environment"
      />
    </>
  );

  // If showModal is true, render in a modal
  if (showModal) {
    return (
      <Modal
        opened={showModal}
        onClose={() => {
          // If read-only, close immediately
          if (isReadOnly) {
            return onCloseModal?.();
          }
          // Guard close when there are changes
          formActionsRef.current?.handleGuardedClose();
        }}
        title={t('consent.title', { ns: 'lims' })}
        size="xl"
        closeOnClickOutside={false}
        closeOnEscape={false}
      >
        {consentForm}
      </Modal>
    );
  }

  // Otherwise render as a regular paper component
  return (
    <Paper shadow="sm" p="md" withBorder pos="relative">
      {consentForm}
    </Paper>
  );
}
